/** Loose helpers for Gateway / SP payloads (PascalCase or camelCase). */

export type Dict = Record<string, unknown>;

export function asDict(value: unknown): Dict {
  return value && typeof value === 'object' ? (value as Dict) : {};
}

export function pick<T = unknown>(obj: unknown, ...keys: string[]): T | undefined {
  const source = asDict(obj);
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) {
      return source[key] as T;
    }
  }

  const lowerKeys = keys.map((k) => k.toLowerCase());
  for (const [prop, value] of Object.entries(source)) {
    if (lowerKeys.includes(prop.toLowerCase()) && value !== undefined && value !== null) {
      return value as T;
    }
  }

  return undefined;
}

export function pickString(obj: unknown, ...keys: string[]): string {
  const value = pick(obj, ...keys);
  return value == null ? '' : String(value);
}

export function pickNumber(obj: unknown, ...keys: string[]): number {
  const value = pick(obj, ...keys);
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function pickDate(obj: unknown, ...keys: string[]): string {
  const value = pick(obj, ...keys);
  if (!value) {
    return '';
  }

  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  // Prefer local calendar date so UTC conversion does not shift SA dates.
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse currency strings such as "R 1,234.56" from SQL FORMAT(). */
export function parseFormattedCurrency(value: unknown): number {
  if (value == null || value === '') {
    return 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const raw = String(value).replace(/[^\d.,-]/g, '').replace(/,/g, '');
  const num = Number(raw);
  return Number.isFinite(num) ? num : 0;
}

export function firstResultSet(payload: unknown): unknown[] {
  const dict = asDict(payload);
  const sets =
    (pick<unknown[]>(dict, 'resultSets', 'ResultSets') as unknown[] | undefined) ?? [];
  const first = sets[0];
  return Array.isArray(first) ? first : [];
}

export function allResultSets(payload: unknown): unknown[][] {
  const dict = asDict(payload);
  const sets =
    (pick<unknown[]>(dict, 'resultSets', 'ResultSets') as unknown[] | undefined) ?? [];
  return sets.map((set) => (Array.isArray(set) ? set : []));
}

export function resultSetAt(payload: unknown, index: number): unknown[] {
  const sets = allResultSets(payload);
  return sets[index] ?? [];
}

/** Returns the first result set whose rows contain any of the given column names. */
export function findResultSetWithColumn(payload: unknown, ...columnNames: string[]): unknown[] {
  for (const set of allResultSets(payload)) {
    if (!set.length) {
      continue;
    }

    const row = asDict(set[0]);
    for (const column of columnNames) {
      if (pick(row, column) !== undefined) {
        return set;
      }
    }
  }

  return [];
}

export function isActiveRow(obj: unknown): boolean {
  const raw = pickString(obj, 'IsActive', 'isActive', 'Status', 'status').toLowerCase();
  if (!raw) {
    return true;
  }
  return raw === '1' || raw === 'true' || raw === 'active';
}

export function procWriteResult(payload: unknown): {
  success: boolean;
  message: string;
} {
  const dict = asDict(payload);
  const summary =
    pick(dict, 'summary', 'Summary') ?? firstResultSet(payload)[0];
  const successRaw = pick(summary, 'Success', 'success');
  const message = pickString(summary, 'Message', 'message');
  const normalized =
    successRaw === true ||
    successRaw === 1 ||
    successRaw === '1' ||
    String(successRaw ?? '').toLowerCase() === 'true';
  const failed =
    successRaw === false ||
    successRaw === 0 ||
    successRaw === '0' ||
    String(successRaw ?? '').toLowerCase() === 'false';

  if (failed) {
    return { success: false, message: message || 'Save failed.' };
  }

  return { success: normalized || successRaw == null, message: message || 'Saved.' };
}
